import { useEffect, useState } from "react";
import api from "../utils/api";
import Navbar from "../components/Navbar";
import { FileText, ExternalLink, Receipt } from "lucide-react";

const STATUS_TONE = { paid: "bg-green-100 text-green-700", sent: "bg-amber-100 text-amber-700", draft: "bg-sage-100 text-sage-600", void: "bg-red-100 text-red-600" };

export default function BillingHistoryPage() {
  const [invoices, setInvoices] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/billing/invoices"), api.get("/billing/transactions")])
      .then(([a, b]) => { setInvoices(a.data); setTxs(b.data); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Billing</p>
          <h1 className="font-heading text-2xl font-semibold text-sage-900">Invoices & Transactions</h1>
        </div>

        <section className="mb-6">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-sage-600 mb-2 flex items-center gap-2"><Receipt className="w-4 h-4" /> Invoices</h2>
          {loading ? <p className="text-sage-500 text-sm">Loading…</p> : invoices.length === 0 ? (
            <div className="bg-white border border-dashed border-sage-200 rounded-md p-6 text-center text-sm text-sage-400">No invoices yet.</div>
          ) : (
            <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead className="bg-sage-50 text-xs uppercase tracking-wider text-sage-600">
                  <tr>
                    <th className="text-left px-4 py-2">#</th>
                    <th className="text-left px-4 py-2">Description</th>
                    <th className="text-right px-4 py-2">Amount</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Due</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(i => (
                    <tr key={i.id} className="border-t border-sage-100">
                      <td className="px-4 py-2 font-mono text-xs">{i.number || i.id.slice(-8)}</td>
                      <td className="px-4 py-2">{i.description}</td>
                      <td className="px-4 py-2 text-right font-semibold">USD {i.amount_usd.toFixed(2)}</td>
                      <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_TONE[i.status] || "bg-sage-100 text-sage-600"}`}>{i.status}</span></td>
                      <td className="px-4 py-2 text-xs text-sage-500">{i.due_at?.slice(0, 10)}</td>
                      <td className="px-4 py-2 text-right space-x-2">
                        {i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-sage-600 hover:underline text-xs inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Pay</a>}
                        {i.invoice_pdf_url && <a href={i.invoice_pdf_url} target="_blank" rel="noreferrer" className="text-sage-600 hover:underline text-xs inline-flex items-center gap-1"><FileText className="w-3 h-3" /> PDF</a>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-sage-600 mb-2">Transactions</h2>
          {txs.length === 0 ? (
            <div className="bg-white border border-dashed border-sage-200 rounded-md p-6 text-center text-sm text-sage-400">No transactions yet.</div>
          ) : (
            <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-sage-50 text-xs uppercase tracking-wider text-sage-600">
                  <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Item</th><th className="text-right px-4 py-2">Amount</th><th className="text-left px-4 py-2">Payment</th></tr>
                </thead>
                <tbody>
                  {txs.map(t => (
                    <tr key={t.id} className="border-t border-sage-100">
                      <td className="px-4 py-2 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2">{t.item_type} · {t.item_key}</td>
                      <td className="px-4 py-2 text-right">USD {t.amount_usd.toFixed(2)}</td>
                      <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_TONE[t.payment_status] || "bg-sage-100 text-sage-600"}`}>{t.payment_status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
